import { useState, useEffect } from "react";
import { saveConsent } from "../../lib/consent/consent.utils";

import type { ConsentPreferences } from "../../types/consent";

const ConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences>({
    necessary: true,
    statistical: false,
    marketing: false,
  });
  const isQrPage = window.location.pathname.includes('/qr/');

  useEffect(() => {
    const consentSeen = document.cookie.includes("consent_banner_seen=true");
    if (!consentSeen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsVisible(true);
    }
  }, []);

  const toggle = (key: keyof ConsentPreferences) => {
    if (key === "necessary") return;
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAction = (data: ConsentPreferences) => {
    saveConsent(data);
    document.cookie = "consent_banner_seen=true; path=/; max-age=31536000";
    setIsVisible(false);
    window.location.reload();
  };

  if (!isVisible || isQrPage) return null;

  return (
    <div className="fixed bottom-0 left-0 z-50 w-full p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white/95 p-6 shadow-2xl backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/95">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
          
          {/* Header & Description */}
          <div className="max-w-md space-y-2">
            <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
              Cookie Settings
            </h3>
            <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400">
              We use cookies to improve your browsing experience. Choose which categories you want to allow.
            </p>
          </div>

          {/* Configuration Toggles */}
          <div className="grid grid-cols-2 gap-4 sm:flex sm:flex-wrap lg:gap-6">
            {(Object.keys(prefs) as Array<keyof ConsentPreferences>).map((key) => (
              <label 
                key={key} 
                className="group flex cursor-pointer items-center gap-3 text-sm font-medium capitalize text-slate-700 dark:text-slate-300"
              >
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={!!prefs[key]}
                    disabled={key === "necessary"}
                    onChange={() => toggle(key)}
                    className="size-5 rounded-md border-slate-300 text-blue-600 transition-all focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
                <span className="group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {key}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Action Footer */}
        <div className="mt-8 flex flex-col gap-3 border-t border-slate-100 pt-6 dark:border-slate-800 sm:flex-row sm:items-center">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleAction({ necessary: true, statistical: true, marketing: true })}
              className="rounded-lg bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition-all hover:bg-slate-800 active:scale-95 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              Accept All
            </button>
            
            <button
              onClick={() => handleAction({ necessary: true, statistical: false, marketing: false })}
              className="rounded-lg bg-slate-100 px-6 py-2.5 text-sm font-semibold text-slate-900 transition-all hover:bg-slate-200 active:scale-95 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700"
            >
              Reject All
            </button>
          </div>

          <button
            onClick={() => handleAction(prefs)}
            className="sm:ml-auto rounded-lg border border-slate-200 px-6 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConsentBanner;
